import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Pledge Notice Page
 *
 * Guides users through the notice filter. Shares the same Notice component
 * as journalist; targets journalist-notice-filter (same data-tour-item).
 */
export const usePledgeNoticeTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnPledgeNotice = pathname === ROUTE_NAMES.PLEDGE_ALL_NOTICES;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeNotice",
      enabled: isOnPledgeNotice,
      steps: [
        {
          id: "pledge-notice-filter",
          targetSelector: '[data-tour-item="journalist-notice-filter"]',
          title: t("tourGuide.pledgeNotice.step1.title"),
          description: t("tourGuide.pledgeNotice.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
      ],
    }),
    [isOnPledgeNotice, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [{ type: TourGuideType.PledgeNotice, isDone: true }],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeNoticeTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeNotice,
    isOnTourPath: isOnPledgeNotice && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeNoticeTourGuide.isActive) {
      setActiveTourModule("pledgeNotice");
      setCurrentTourStep(pledgeNoticeTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeNoticeTourGuide.isActive,
    pledgeNoticeTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeNoticeTourGuide,
    isMobile,
  };
};
